import type { HealthState } from '@itr/shared';

/**
 * Speaks a short alert announcement via the browser's built-in
 * text-to-speech (SpeechSynthesis) — the audible companion to the two-tone
 * chime in chime.ts. Same rationale as the chime: no bundled audio asset, no
 * server round trip, and it only works while a tab is open, under the same
 * autoplay/user-gesture rules as any audio.
 */

const STORAGE_KEY = 'itr.alertSpeech';

export function isAlertSpeechEnabled(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

export function setAlertSpeechEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEY, enabled ? '1' : '0');
  } catch {
    // Best-effort — the toggle just won't survive a reload.
  }
}

export function isAlertSpeechSupported(): boolean {
  return typeof window !== 'undefined' && 'speechSynthesis' in window;
}

/**
 * Cancels any utterance still in flight before queuing the next one — a
 * burst of transitions (a whole site flapping at once) should read as the
 * latest state, not a backlog of stale announcements piling up behind it.
 */
export function speakAlert(text: string): void {
  if (!isAlertSpeechSupported()) return;
  try {
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 1;
    utterance.volume = 1;
    window.speechSynthesis.speak(utterance);
  } catch {
    // Speech is a bonus channel; never let it break the caller.
  }
}

const HEALTH_SPOKEN: Record<HealthState, string> = {
  up: 'up',
  degraded: 'degraded',
  down: 'down',
  maintenance: 'in maintenance',
  unknown: 'in an unknown state',
};

/** "Acme Corp, site HQ" — whichever of the two is known, or '' for neither. */
export function alertLocation(customerName: string | null, siteName: string | null): string {
  return [customerName, siteName ? `site ${siteName}` : null].filter(Boolean).join(', ');
}

/**
 * Builds the sentence for a watched resource's health transition. `up` is
 * the one state that reads as good news ("cleared"); everything else is a
 * fresh or worsening problem and reads as a fired alert. The client and site
 * lead, so whoever hears it knows whose problem it is before the device name.
 */
export function healthTransitionText(
  name: string,
  health: HealthState,
  customerName: string | null = null,
  siteName: string | null = null,
): string {
  const where = alertLocation(customerName, siteName);
  const prefix = health === 'up' ? 'Alert cleared' : 'Alert';
  const lead = where ? `${prefix} for ${where}` : prefix;
  return health === 'up' ? `${lead}: ${name} is back up.` : `${lead}: ${name} is ${HEALTH_SPOKEN[health]}.`;
}

export function speakHealthTransition(
  name: string,
  health: HealthState,
  customerName: string | null = null,
  siteName: string | null = null,
): void {
  speakAlert(healthTransitionText(name, health, customerName, siteName));
}
